import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faLinkedin,
  faFacebook,
  faInstagram,
  faXTwitter,
  faYoutube,
  faTiktok,
  faPinterest,
  faThreads,
  faGoogle,
  faBluesky,
} from '@fortawesome/free-brands-svg-icons';
import { faGlobe } from '@fortawesome/free-solid-svg-icons';
import { PeriodPills } from './PeriodPills';
import { HorizontalCardCarousel } from './HorizontalCardCarousel';
import { Icon } from '@/components/ui/Icon';
import { Tooltip } from '@/components/ui/Tooltip';
import { HoverCard } from '@/components/ui/HoverCard';
import { RetryImage } from '@/components/ui/RetryImage';
import { PlaceholderImage } from '@/components/ui/PlaceholderImage';
import { DashboardPostPopover } from './DashboardPostPopover';
import { ICONS } from '@/constants/icons';
import { cn } from '@/utils/cn';
import { usePublishingQueue, PublishingQueueItem } from '@/hooks/useDashboardStats';
import { useTimezone } from '@/contexts/TimezoneContext';

/**
 * Publishing Section - Posts scheduled for publishing (Carousel layout)
 *
 * Features:
 * - Horizontal carousel with arrow navigation
 * - Period filtering (today/week/month)
 * - Real-time status indicators
 * - FontAwesome platform icons
 * - Caption-based display (no titles)
 * - HoverCard popover matching CalendarPostPopover style
 * - Timezone-aware scheduling times
 */

type Period = 'today' | 'week' | 'month';

// FontAwesome platform icons (backend returns UPPERCASE platform names)
const PLATFORM_FA_ICONS: Record<string, IconDefinition> = {
  LINKEDIN: faLinkedin,
  FACEBOOK: faFacebook,
  INSTAGRAM: faInstagram,
  TWITTER: faXTwitter,
  YOUTUBE: faYoutube,
  TIKTOK: faTiktok,
  PINTEREST: faPinterest,
  THREADS: faThreads,
  GOOGLE_MY_BUSINESS: faGoogle,
  BLUESKY: faBluesky,
};

// Platform colors for icons
const PLATFORM_COLORS: Record<string, string> = {
  LINKEDIN: '#0A66C2',
  FACEBOOK: '#1877F2',
  INSTAGRAM: '#E4405F',
  TWITTER: '#000000',
  YOUTUBE: '#FF0000',
  TIKTOK: '#000000',
  PINTEREST: '#E60023',
  THREADS: '#000000',
  GOOGLE_MY_BUSINESS: '#4285F4',
  BLUESKY: '#0085FF',
};

// Status indicator colors for card dots (matching calendar)
const statusColors: Record<string, string> = {
  queued: 'bg-teal-400',
  picked_up: 'bg-amber-400',
  publishing: 'bg-yellow-400 animate-pulse',
};

// Status mapping for HoverCard gradient headers (matching CalendarPostPopover exactly)
// `labelKey`/`labelDefault` are resolved via t() inside PostCard (Task #182 i18n fix) —
// reuses the existing `common:status_*` keys rather than hardcoded English.
const STATUS_MAP: Record<string, { gradient: string; icon: string; labelKey: string; labelDefault: string }> = {
  queued: { gradient: 'from-teal-500 to-teal-600', icon: ICONS.CLOCK, labelKey: 'common:status_scheduled', labelDefault: 'Scheduled' },
  picked_up: { gradient: 'from-amber-500 to-orange-500', icon: ICONS.CLOCK, labelKey: 'common:status_processing', labelDefault: 'Processing' },
  publishing: { gradient: 'from-yellow-500 to-orange-500', icon: ICONS.LOADER, labelKey: 'common:status_publishing', labelDefault: 'Publishing' },
};

/**
 * Skeleton Card Component for Loading State
 */
const SkeletonCard = () => (
  <div className="flex-none w-[160px] aspect-square bg-gray-200 rounded-xl animate-pulse" />
);

/**
 * Post Card Component
 */
interface PostCardProps {
  item: PublishingQueueItem;
}

const PostCard = ({ item }: PostCardProps) => {
  const { t } = useTranslation(['dashboard', 'common']);
  const { formatTimeOnly, formatRelativeDate } = useTimezone();
  const navigate = useNavigate();

  const handleClick = () => {
    navigate('/calendar');
  };

  const statusDotColor = statusColors[item.status] || 'bg-gray-400';
  const statusInfo = STATUS_MAP[item.status] || {
    gradient: 'from-gray-400 to-gray-500',
    icon: ICONS.CLOCK,
    // NEW key — not yet in locale JSON, reported to locale-owner (Task #182)
    labelKey: 'common:status_unknown',
    labelDefault: 'Unknown',
  };

  // HoverCard popover content (matching CalendarPostPopover style exactly)
  const popoverContent = (
    <DashboardPostPopover
      gradient={statusInfo.gradient}
      headerIcon={statusInfo.icon}
      headerLabel={t(statusInfo.labelKey, { defaultValue: statusInfo.labelDefault })}
      headerLines={[
        { icon: ICONS.CALENDAR, text: item.calendarName },
        { icon: ICONS.CLOCK, text: formatTimeOnly(item.scheduledAt) },
      ]}
      postMedia={item.postMedia || []}
      caption={item.caption || ''}
    >
      {/* Channels section */}
      {item.channels.length > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
            {t('common:publishing_channels')} ({item.channels.length})
          </p>
          <div className="flex flex-wrap gap-2.5">
            {item.channels.map((ch, i) => (
              <Tooltip
                key={i}
                content={
                  <div className="text-xs">
                    <p className="font-medium">{ch.platformUsername || ch.platform}</p>
                    <p className="text-gray-400">{ch.platform}</p>
                  </div>
                }
                className="!z-[10001]"
              >
                <div className="relative">
                  {/* Avatar/Icon circle */}
                  <div className={cn(
                    'w-8 h-8 rounded-full overflow-hidden flex items-center justify-center',
                    'ring-2 ring-offset-1 ring-primary-600',
                  )}>
                    {ch.profilePhotoUrl ? (
                      <img src={ch.profilePhotoUrl} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                        <FontAwesomeIcon
                          icon={PLATFORM_FA_ICONS[ch.platform] || faGlobe}
                          className="text-gray-500 w-4 h-4"
                          aria-hidden="true"
                        />
                      </div>
                    )}
                  </div>
                  {/* Small platform badge overlay (bottom-right) */}
                  <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-white shadow-sm flex items-center justify-center">
                    <FontAwesomeIcon
                      icon={PLATFORM_FA_ICONS[ch.platform] || faGlobe}
                      className="text-gray-600 text-[10px]"
                      aria-hidden="true"
                    />
                  </div>
                </div>
              </Tooltip>
            ))}
          </div>
        </div>
      )}
    </DashboardPostPopover>
  );

  // Card trigger element - Square layout with full-bleed thumbnail
  const cardTrigger = (
    <div
      onClick={handleClick}
      className="flex-none w-[160px] aspect-square rounded-xl overflow-hidden relative bg-gray-100 cursor-pointer hover:shadow-lg hover:scale-[1.03] transition-all duration-200"
    >
      {/* Full-bleed thumbnail */}
      {item.thumbnailUrl ? (
        <RetryImage
          src={item.thumbnailUrl}
          alt=""
          className="w-full h-full object-cover"
          fallback={<PlaceholderImage />}
        />
      ) : (
        <PlaceholderImage />
      )}

      {/* Top overlay: Channel avatars + status dot */}
      <div className="absolute top-0 inset-x-0 p-2 flex items-center gap-1">
        {item.channels.slice(0, 3).map((ch, index) => (
          <Tooltip key={`${ch.platform}-${index}`} content={ch.platformUsername || ch.platform}>
          <div
            className="w-6 h-6 rounded-full overflow-hidden bg-white/90 backdrop-blur-sm flex items-center justify-center flex-shrink-0 ring-1 ring-white/50"
          >
            {ch.profilePhotoUrl ? (
              <img src={ch.profilePhotoUrl} alt="" className="w-full h-full object-cover" />
            ) : (
              <FontAwesomeIcon
                icon={PLATFORM_FA_ICONS[ch.platform] || faGlobe}
                className="w-3 h-3"
                style={{ color: PLATFORM_COLORS[ch.platform] || '#6B7280' }}
                aria-hidden="true"
              />
            )}
          </div>
          </Tooltip>
        ))}
        {item.channels.length > 3 && (
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-white/90 backdrop-blur-sm text-[10px] font-semibold text-gray-600 ring-1 ring-white/50">
            +{item.channels.length - 3}
          </span>
        )}
        {/* Status dot - top right */}
        <div className="ml-auto">
          <div className={cn('w-2.5 h-2.5 rounded-full ring-2 ring-white/50', statusDotColor)} />
        </div>
      </div>

      {/* Bottom gradient overlay */}
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent p-2 pt-8">
        {/* Calendar name badge */}
        <div className="flex items-center gap-1 mb-0.5">
          <Icon name={ICONS.CALENDAR} size="xs" className="text-white" ariaHidden />
          <span className="text-[11px] text-white font-medium drop-shadow-sm">
            {item.calendarName}
          </span>
        </div>
        {/* Date/time */}
        <div className="flex items-center gap-1 mb-0.5">
          <Icon name={ICONS.CLOCK} size="xs" className="text-white" ariaHidden />
          <span className="text-[11px] text-white font-medium drop-shadow-sm">
            {formatRelativeDate(item.scheduledAt)}
          </span>
        </div>
        {/* Caption */}
        <p className={cn(
          'text-[11px] text-white/90 line-clamp-1 drop-shadow-sm',
          !item.caption && 'italic'
        )}>
          {item.caption ? (item.caption.length > 40 ? `${item.caption.slice(0, 40)}...` : item.caption) : t('common:publishing_no_caption')}
        </p>
      </div>
    </div>
  );

  return (
    <HoverCard
      trigger={cardTrigger}
      side="right"
      align="start"
    >
      {popoverContent}
    </HoverCard>
  );
};

/**
 * Empty State Component
 */
const EmptyState = () => {
  const { t } = useTranslation('dashboard');

  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="w-16 h-16 bg-gray-100 rounded-full grid place-items-center mb-4">
        <Icon name={ICONS.CALENDAR} size="xl" color="text-gray-400" ariaHidden />
      </div>
      <p className="text-gray-500 text-sm">
        {t('dashboard_publishing_empty', { defaultValue: 'No posts in queue for this period' })}
      </p>
    </div>
  );
};

/**
 * Main Publishing Section Component
 */
export const PublishingSection = () => {
  const { t } = useTranslation(['dashboard', 'common']);
  const navigate = useNavigate();
  const [period, setPeriod] = useState<Period>('today');
  const { data, isLoading } = usePublishingQueue(period);

  const handleViewAll = () => {
    navigate('/creator?publishingStatus=scheduled');
  };

  return (
    <div>
      {/* Period Filter - tighter spacing */}
      <div className="mb-3 flex justify-end">
        <PeriodPills value={period} onChange={setPeriod} count={data?.items.length} />
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex gap-3 overflow-x-hidden">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      )}

      {/* Empty State */}
      {!isLoading && data?.items.length === 0 && <EmptyState />}

      {/* Carousel */}
      {!isLoading && data && data.items.length > 0 && (
        <HorizontalCardCarousel>
          {data.items.map((item) => (
            <PostCard key={item.id} item={item} />
          ))}
        </HorizontalCardCarousel>
      )}

      {/* View all link - tighter */}
      <div className="mt-3 text-center">
        <button
          onClick={handleViewAll}
          className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
        >
          {t('dashboard_publishing_view_all', { defaultValue: 'View all scheduled posts →' })}
        </button>
      </div>
    </div>
  );
};
